// Caches the current user's grant for one named capability. Uses
// staff.has_my_capability(text) (migration 6085, SECURITY DEFINER,
// self-scoped via auth.uid()) so it works for any authenticated user without
// the portal needing to read its own session's user id.
//
// Returns:
//   - hasCapability: true | false  (after load)
//   - loading: true while the RPC is in-flight
//   - error:   set if the RPC fails
//
// One fetch per (mount, capability). Pages that gate a whole screen on a
// capability (e.g. #761's Map Unmatched Names, crm.map_aliases) call this
// once at the top and branch on the result — the same shape as useIsAdmin().

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface UseHasCapabilityResult {
  hasCapability: boolean;
  loading: boolean;
  error: string | null;
}

export function useHasCapability(capability: string): UseHasCapabilityResult {
  const [state, setState] = useState<UseHasCapabilityResult>({
    hasCapability: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    // staff.has_my_capability(text) is SECURITY DEFINER and granted to
    // authenticated. Calling .rpc on the staff schema requires .schema('staff')
    // first — same pattern as useIsAdmin's staff.is_admin().
    //
    // Wrapped in try/catch (Promise.resolve() converts the rpc's PromiseLike to
    // a real Promise) for the same reason useIsAdmin does: an auto-refresh race
    // in dev occasionally surfaces as `TypeError: Failed to fetch`, and that
    // should land in state.error rather than as an unhandled rejection.
    (async () => {
      try {
        const { data, error } = await Promise.resolve(
          supabase.schema("staff").rpc("has_my_capability", { p_capability: capability }),
        );
        if (cancelled) return;
        if (error) {
          setState({ hasCapability: false, loading: false, error: error.message });
          return;
        }
        setState({ hasCapability: Boolean(data), loading: false, error: null });
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setState({ hasCapability: false, loading: false, error: msg });
      }
    })();
    return () => { cancelled = true; };
  }, [capability]);

  return state;
}
